import { FRESH_LOGIN_PARAM, ssoStorage } from '../sso'
import { buildLoggedOutUrl, cognitoSignOutRedirectUri } from './loggedOutUrl'

/**
 * Pathname Cognito returns to after a sign-out.
 *
 * Read lazily rather than at module load: CRA inlines REACT_APP_* at build
 * time, so a module-level constant is fine in a browser but freezes before a
 * test can set the variable.
 */
const signOutLandingPath = (): string | null => {
  try {
    return new URL(cognitoSignOutRedirectUri() ?? '').pathname
  } catch {
    return null
  }
}

/**
 * Decides where a fresh page load has to be sent, given what the last redirect
 * left behind.
 *
 * Both flows that hop through Cognito /logout come back to the same registered
 * sign-out URL, carrying nothing, so the only way to tell them apart is the
 * hand-off each one wrote before leaving:
 *
 * - a fresh-login hop (SSOLandingPage clearing a stale Hosted UI session)
 *   resumes at the authorize step;
 * - a sign-out goes to the logged-out page, with the SSO client put back into
 *   the URL as ?source=.
 *
 * Returns null when this load is not the tail of either redirect. Both
 * hand-offs are read-and-clear, so this must be called once per load.
 */
export const resolveBootstrapRedirect = (
  pathname: string,
  hasAccessToken: boolean
): string | null => {
  // Resuming a fresh login wins: it is mid-sign-in, and the sign-out branch
  // would strand it on the logged-out page.
  const pendingFreshLogin = ssoStorage.getAndClearPendingFreshLogin()
  if (pendingFreshLogin) {
    const params = new URLSearchParams(pendingFreshLogin.search)
    params.set(FRESH_LOGIN_PARAM, '1')
    return `/sso/${pendingFreshLogin.clientName}?${params.toString()}`
  }

  // Fails closed. An unset or unparsable variable used to skip this check
  // entirely, which let any page load consume the hand-off and bounce to the
  // logged-out page -- the same over-broad forward this guard exists to stop.
  // Nothing is lost by bailing: a logout that could not reach Cognito never
  // writes a hand-off, it puts ?source= straight in the URL instead.
  const landingPath = signOutLandingPath()
  if (!landingPath || pathname !== landingPath) return null
  // An authenticated load on the landing path is not a sign-out landing, and
  // the read below consumes the hand-off -- bail before it rather than bouncing
  // a working session to the logged-out page.
  if (hasAccessToken) return null

  const pendingLogout = ssoStorage.getAndClearPendingLogoutRedirect()
  if (!pendingLogout) return null
  return buildLoggedOutUrl(pendingLogout.clientName, pendingLogout.cognitoSessionCleared)
}

/**
 * Whether this load looks like the tail of a redirect -- read-only, so it is
 * safe to call during render.
 *
 * Lets App hold its first paint back only on the loads that are about to be
 * forwarded, instead of consuming the hand-offs in a render that React is free
 * to throw away and re-run.
 */
export const isBootstrapRedirectPending = (pathname: string, hasAccessToken: boolean): boolean => {
  if (ssoStorage.peekPendingFreshLogin()) return true
  const landingPath = signOutLandingPath()
  if (!landingPath || pathname !== landingPath) return false
  if (hasAccessToken) return false
  return Boolean(ssoStorage.peekPendingLogoutRedirect())
}
